import * as React from "react";
import formatDate from "dateformat";

import ContentCard from "../../../../ui-library/content-card/content-card";
import CardHeading from "../../../../ui-library/content-card/card-heading";
import CardContent from "../../../../ui-library/content-card/card-content";
import ItemPicker from "../../../../ui-library/item-picker/item-picker";
import { EventCategory } from "../../../../../utils/analytics";
import { RainBow, Colors } from "../../../../design-tokens/colors";
import CardSubHeading from "../../../../ui-library/content-card/card-sub-heading";
import Sorter from "../../../../ui-library/sorter/sorter";
import SourceLink from "../../../../ui-library/source-link/source-link";
import LinkButton from "../../../../ui-library/link-button/link-button";
import {
  addUrlParameterToLocation,
  getUrlParameterByName,
} from "../../../../../utils/url";
import { useUSStatesCovidCases } from "../../../../../hooks/static-queries/use-us-states-covid-cases";
import { getPerformanceByCases } from "../../../../../utils/get-country-performance";
import { getCOVIDIntentUrl } from "../../../../../utils/question-url";
import { SPECIAL_INTENTS } from "../../../../../constants/special-intents";
import { COVID_SOURCES } from "../../../../../constants/sources";
import styles from "./countries-control-snapshots.styles";
import StateSituationChartGroup, {
  StateData,
  SortBy,
} from "./chart-card-group/state-situation-chart-group";

const tabs = ["Doing well", "Getting there", "Not doing well"];
const sorters: SortBy[] = ["A – Z", "Total cases", "Avg. new daily"];

interface USStatesControlSnapshotsProps {
  top4Only?: boolean;
}

const USStatesControlSnapshots: React.FC<USStatesControlSnapshotsProps> = ({
  top4Only,
}) => {
  const [activeTab, setActiveTab] = React.useState<string>(tabs[0]);
  const [activeSorter, setActiveSorter] = React.useState<SortBy>(sorters[0]);

  const urlParamName = "activeCUSSCSTab";

  const handleSetTab = (value: string) => {
    addUrlParameterToLocation(urlParamName, value);
    setActiveTab(value);
  };

  React.useEffect(() => {
    const activeTabFromUrl = getUrlParameterByName(urlParamName);
    if (activeTabFromUrl && tabs.includes(activeTabFromUrl)) {
      setActiveTab(activeTabFromUrl);
    }
  }, []);

  const url = `${getCOVIDIntentUrl(
    SPECIAL_INTENTS.USSituationSnapshot.alias
  )}?${urlParamName}=${encodeURIComponent(activeTab)}`;

  const statesData =
    (useUSStatesCovidCases()?.filter(
      (s) => s?.data?.length && s.data.length > 10
    ) as StateData[]) || [];

  const lastDay = statesData && statesData[0] && statesData[0].latest.date;

  if (!lastDay) {
    return null;
  }

  const doingWell: StateData[] = [];
  const gettingThere: StateData[] = [];
  const notWell: StateData[] = [];

  statesData?.forEach((s) => {
    const { latest, data } = s;

    const { performance } = getPerformanceByCases({
      latest,
      data,
    });

    if (performance === "doing well") {
      doingWell.push(s);
    } else if (performance === "getting there") {
      gettingThere.push(s);
    } else {
      notWell.push(s);
    }
  });

  const headingDetail = `Last updated ${formatDate(lastDay, "dd mmm yyyy")}`;

  return (
    <div>
      <ContentCard>
        <CardContent>
          <CardHeading headingDetail={headingDetail}>
            US states COVID-19 situation snapshots
          </CardHeading>
          <CardSubHeading>
            Graphs show a 7 day rolling average of daily new cases of COVID-19.
          </CardSubHeading>

          <ItemPicker
            itemPickerName="USStatesControlItemPicker"
            isUniWidth={true}
            items={tabs.map((t) => ({
              label: t,
              value: t,
            }))}
            activeItemValue={activeTab}
            onItemClick={handleSetTab}
            eventCategory={EventCategory.CountryDoingWellNNotWell}
          />

          <CardSubHeading>
            <div css={styles.sectionHeading}>
              <div css={activeTab !== tabs[0] && styles.hidden}>
                <strong css={{ color: RainBow.GrassGreen }}>
                  {doingWell.length} green states
                </strong>{" "}
                are keeping case numbers low with very few new daily cases.
              </div>
              <div css={activeTab !== tabs[1] && styles.hidden}>
                <strong css={{ color: Colors.Orange }}>
                  {gettingThere.length} orange states
                </strong>{" "}
                are declining with fewer daily new cases but still need to do
                more.
              </div>
              <div css={activeTab !== tabs[2] && styles.hidden}>
                <strong css={{ color: Colors.Red }}>
                  {notWell.length} red states
                </strong>{" "}
                are either going up, staying constantly high or going down too
                slow.
              </div>

              <div css={styles.sorterContainer}>
                <Sorter
                  items={sorters}
                  value={activeSorter}
                  onSelect={(value) => setActiveSorter(value as SortBy)}
                  eventCategory={EventCategory.CountryDoingWellNNotWell}
                />
              </div>
            </div>
          </CardSubHeading>

          {activeTab === tabs[0] && (
            <StateSituationChartGroup
              statesData={doingWell}
              color={RainBow.GrassGreen}
              sortyBy={activeSorter}
              size={top4Only ? 4 : undefined}
              isMobileInline={top4Only}
            />
          )}
          {activeTab === tabs[1] && (
            <StateSituationChartGroup
              statesData={gettingThere}
              color={Colors.Orange}
              sortyBy={activeSorter}
              size={top4Only ? 4 : undefined}
              isMobileInline={top4Only}
            />
          )}
          {activeTab === tabs[2] && (
            <StateSituationChartGroup
              statesData={notWell}
              color={Colors.Red}
              sortyBy={activeSorter}
              size={top4Only ? 4 : undefined}
              isMobileInline={top4Only}
            />
          )}

          {!top4Only && (
            <CardSubHeading>
              <div css={activeTab !== tabs[0] && styles.hidden}>
                These states have less than or equal to 20 average new reported
                daily cases, and not at early stage of transimission.
              </div>
              <div css={activeTab !== tabs[1] && styles.hidden}>
                These states are keeping infection rate under 1 in the past 10
                days, and have less than 1,000 average new daily cases.
              </div>
              <div css={activeTab !== tabs[2] && styles.hidden}>
                These states are not controlling infection rate well in the past
                10 days, or have more than or equal to 1,000 average new daily
                cases.
              </div>
            </CardSubHeading>
          )}

          {top4Only && (
            <LinkButton
              href={url}
              isCenter={true}
              analytics={{
                category: EventCategory.USStatesControlSnapshots,
                action: "Click - Explore all states",
                label: "Explore all states",
              }}
            >
              Explore all states
            </LinkButton>
          )}

          {!top4Only && (
            <SourceLink
              sources={[COVID_SOURCES.NYT]}
              isDataOnly={true}
              eventCategory={EventCategory.USStatesControlSnapshots}
            />
          )}
        </CardContent>
      </ContentCard>
    </div>
  );
};

export default USStatesControlSnapshots;
